import React from "react";
import {
  Box,
  Drawer,
  Typography,
  Checkbox,
  FormControlLabel,
  Button,
  useTheme,
} from "@mui/material";
import CheckIcon from "@mui/icons-material/Check";
import { colors } from "../values/colors";

const FilterSidebar = ({
  open,
  onClose,
  roles = [], // [{id, name}]
  teams = [], // ["Team A", ...]
  tempRoles = [], // IDs
  tempTeams = [],
  setTempRoles,
  setTempTeams,
  onApplyFilters,
  onResetFilters,
}) => {
  const theme = useTheme();

  const UncheckedBox = (
    <Box
      sx={{
        width: 20,
        height: 20,
        borderRadius: "3px",
        border: `2px solid ${theme.palette.grey[400]}`,
        display: "inline-block",
        mr: "8px",
      }}
    />
  );

  const CheckedBox = (
    <Box
      sx={{
        width: 20,
        height: 20,
        borderRadius: "3px",
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        mr: "8px",
        backgroundImage: colors.linearGradent,
        boxShadow: "0 1px 3px rgba(0,0,0,0.15)",
      }}
    >
      <CheckIcon sx={{ fontSize: 16, color: "#fff" }} />
    </Box>
  );

  // Toggle Role IDs
  const toggleTempRole = (roleId) => {
    setTempRoles((prev) =>
      prev.includes(roleId)
        ? prev.filter((id) => id !== roleId)
        : [...prev, roleId]
    );
  };

  const toggleTempTeam = (team) => {
    setTempTeams((prev) =>
      prev.includes(team) ? prev.filter((t) => t !== team) : [...prev, team]
    );
  };

  return (
    <Drawer
      anchor="left"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: 320,
          borderRadius: "0px 18px 18px 0px",
          p: 2,
        },
      }}
    >
      {/* ROLE HEADER */}
      <Typography fontWeight={600} fontSize={22} mt={1} mb={1}>
        Category
      </Typography>

      {/* ROLE LIST */}
      <Box
        sx={{
          maxHeight: 200,
          overflowY: "hidden",
          pr: 1,
          mb: 3,
          py: 1,
        }}
      >
        {roles.map((role) => (
          <FormControlLabel
            key={role.id}
            sx={{ display: "flex", m: 0, mb: 1 }}
            control={
              <Checkbox
                disableRipple
                icon={UncheckedBox}
                checkedIcon={CheckedBox}
                checked={tempRoles.includes(role.id)}
                onChange={() => toggleTempRole(role.id)}
                sx={{ p: 0.4, color: "transparent" }}
              />
            }
            label={<Typography fontSize={18}>{role.name}</Typography>}
          />
        ))}
      </Box>

      {/* TEAM HEADER */}
      <Typography fontWeight={600} fontSize={22} mb={1} mt={1}>
        Team
      </Typography>

      {/* TEAM LIST */}
      <Box
        sx={{
          maxHeight: 540,
          overflowY: "auto",
          overflowX: "hidden",
          pr: 1,
          mb: 3,
          py: 0.5,
          pl: 1.5,

          /* ★ CUSTOM SCROLLBAR */
          "&::-webkit-scrollbar": {
            width: "6px",
          },
          "&::-webkit-scrollbar-track": {
            backgroundColor: "#f0f0f0",
            borderRadius: "10px",
          },
          "&::-webkit-scrollbar-thumb": {
            background: colors.linearGradent,
            borderRadius: "10px",
          },
          "&::-webkit-scrollbar-thumb:hover": {
            opacity: 0.9,
          },

          /* For Firefox */
          scrollbarWidth: "thin",
          scrollbarColor: "#ff7a18 #f0f0f0",
        }}
      >
        {teams.map((team, index) => (
          <FormControlLabel
            key={index}
            sx={{
              display: "flex", // <-- forces full row
              width: "100%", // <-- prevents side-by-side
              pb: 0.7,
            }}
            control={
              <Checkbox
                disableRipple
                icon={UncheckedBox}
                checkedIcon={CheckedBox}
                checked={tempTeams.includes(team)}
                onChange={() => toggleTempTeam(team)}
                sx={{ p: 0.4, color: "transparent" }}
              />
            }
            label={<Typography fontSize={18}>{team}</Typography>}
          />
        ))}
      </Box>

      {/* BUTTONS */}
      <Box sx={{ display: "flex", gap: 1.5 }}>
        <Button
          variant="contained"
          fullWidth
          onClick={onApplyFilters}
          sx={{
            textTransform: "none",
            fontWeight: 600,
            borderRadius: "50px",
            backgroundImage: colors.linearGradent,
            color: "#fff",
            py: 1,
          }}
        >
          Apply
        </Button>

        <Button
          variant="outlined"
          fullWidth
          onClick={onResetFilters}
          sx={{
            textTransform: "none",
            fontWeight: 600,
            borderRadius: "50px",
            backgroundColor: "#EDEDEE",
            borderColor: "#EDEDEE",
            color: "#757575",
            py: 1,
          }}
        >
          Reset
        </Button>
      </Box>
    </Drawer>
  );
};

export default FilterSidebar;
